import { App } from "@capacitor/app"
import { Capacitor, type PluginListenerHandle } from "@capacitor/core"
import { AppUpdate } from "@capawesome/capacitor-app-update"
import { showUpdateAvailableDialog } from "@/services/app-update-dialog"
import {
  nativeStoreUpdateFromInfo,
  isNativeStoreUpdateSkippable
} from "./native-store-update-policy"

const FOREGROUND_UPDATE_CHECK_INTERVAL_MS = 60 * 1000

let listenersInitialized = false
let startupComplete = false
let promptVisible = false
let nativeUpdateAvailable = false
let firstCheckCompleted = false
let startupUpdateOutstanding = false
let checkInProgress = false
let dismissedKey: string | null = null
let listenerHandles: PluginListenerHandle[] = []
let foregroundUpdateCheckInterval: ReturnType<typeof setInterval> | null = null

export function isNativeStoreUpdateAvailable(): boolean {
  return nativeUpdateAvailable
}

function startForegroundUpdateChecks() {
  stopForegroundUpdateChecks()
  foregroundUpdateCheckInterval = setInterval(() => {
    void checkForNativeStoreUpdate()
  }, FOREGROUND_UPDATE_CHECK_INTERVAL_MS)
}

function stopForegroundUpdateChecks() {
  if (foregroundUpdateCheckInterval) {
    clearInterval(foregroundUpdateCheckInterval)
    foregroundUpdateCheckInterval = null
  }
}

async function showNativeStoreUpdateDialog(
  update: { key: string; versionLabel: string | null },
  skippable: boolean
) {
  if (promptVisible) return
  if (skippable && dismissedKey === update.key) return

  promptVisible = showUpdateAvailableDialog({
    version: update.versionLabel,
    skippable,
    onConfirm: async () => {
      promptVisible = false
      try {
        await AppUpdate.openAppStore()
      } catch (error) {
        console.error("[StoreUpdate] Failed to open Play Store / App Store", error)
        await showNativeStoreUpdateDialog(update, skippable)
      }
    },
    onCancel: () => {
      promptVisible = false
      dismissedKey = update.key
    }
  })
}

export async function checkForNativeStoreUpdate() {
  if (!startupComplete || checkInProgress) return

  checkInProgress = true
  try {
    const info = await AppUpdate.getAppUpdateInfo()
    const update = nativeStoreUpdateFromInfo(info)
    nativeUpdateAvailable = update != null

    const skippable = isNativeStoreUpdateSkippable(firstCheckCompleted, startupUpdateOutstanding)

    if (!update) {
      startupUpdateOutstanding = false
      firstCheckCompleted = true
      return
    }

    if (!firstCheckCompleted) {
      startupUpdateOutstanding = true
    }
    firstCheckCompleted = true

    await showNativeStoreUpdateDialog(update, skippable)
  } catch (error) {
    console.error("[StoreUpdate] Failed to check Play Store / App Store version", error)
  } finally {
    checkInProgress = false
  }
}

async function onAppForegrounded() {
  if (startupComplete) {
    await checkForNativeStoreUpdate()
    startForegroundUpdateChecks()
  }
}

function onAppBackgrounded() {
  stopForegroundUpdateChecks()
}

export function markNativeStoreStartupComplete() {
  startupComplete = true
  startForegroundUpdateChecks()
  void checkForNativeStoreUpdate()
}

export async function initializeNativeStoreUpdatePrompt() {
  if (!Capacitor.isNativePlatform() || listenersInitialized) return

  listenersInitialized = true
  listenerHandles.push(
    await App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void onAppForegrounded()
      } else {
        onAppBackgrounded()
      }
    })
  )

  if (startupComplete) {
    await onAppForegrounded()
  }
}

export async function teardownNativeStoreUpdatePrompt() {
  stopForegroundUpdateChecks()
  await Promise.all(listenerHandles.map((handle) => handle.remove()))
  listenerHandles = []
  listenersInitialized = false
  startupComplete = false
  promptVisible = false
  nativeUpdateAvailable = false
  firstCheckCompleted = false
  startupUpdateOutstanding = false
  checkInProgress = false
  dismissedKey = null
}
